import { useMemo, useState } from 'react';
import { Skeleton } from '../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../shared/components/SkeletonRegion';
import { useVehicleRig } from '../hooks/use-vehicle-rig';
import { trailerSubject, truckSubject } from '../policies/couple-options';
import { tripNotice } from '../policies/coupling-labels';
import { CoupleTrailerDrawer } from './CoupleTrailerDrawer';
import { RigCard } from './RigCard';
import { UncoupleTrailerModal } from './UncoupleTrailerModal';

interface VehicleRigPanelProps {
  vehicleKind: 'truck' | 'trailer';
  vehicleId: string;
  canManage: boolean;
  /** After a couple or uncouple, so the page's coupling history and lists catch up. */
  onChanged?: () => void;
}

/** The head of a truck's or trailer's own page: its rig, and the couple, swap and uncouple actions (gitaumoses4/chukua#390). */
export function VehicleRigPanel({ vehicleKind, vehicleId, canManage, onChanged }: VehicleRigPanelProps) {
  const { state, reload } = useVehicleRig(vehicleKind, vehicleId);
  const [open, setOpen] = useState<'couple' | 'uncouple' | null>(null);
  const rig = state.status === 'loaded' ? state.rig : null;
  const subject = useMemo(() => {
    if (!rig) return null;
    return vehicleKind === 'truck' ? truckSubject(rig) : trailerSubject(rig);
  }, [vehicleKind, rig]);

  if (state.status === 'loading') {
    return (
      <SkeletonRegion label="Loading rig" className="mb-4 rounded-lg border border-line bg-surface p-4">
        <Skeleton className="h-24 w-full" />
      </SkeletonRegion>
    );
  }
  if (!rig) return null;

  const changed = () => {
    void reload();
    onChanged?.();
  };

  return (
    <>
      <RigCard vehicleKind={vehicleKind} rig={rig} canManage={canManage} onCouple={() => setOpen('couple')} onUncouple={() => setOpen('uncouple')} />
      {open === 'couple' && subject && (
        <CoupleTrailerDrawer subject={subject} tripNotice={tripNotice(rig.truck)} onClose={() => setOpen(null)} onCoupled={changed} />
      )}
      {open === 'uncouple' && rig.couplingId && rig.truck && rig.trailer && (
        <UncoupleTrailerModal
          coupling={{ id: rig.couplingId, truckRegistration: rig.truck.registration, trailerRegistration: rig.trailer.registration }}
          tripNotice={tripNotice(rig.truck)}
          onClose={() => setOpen(null)}
          onUncoupled={changed}
        />
      )}
    </>
  );
}
